import { useState, useEffect } from "react";
import {
  Settings,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  DollarSign,
  Activity,
  Pill,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import medicalDB from "../services/database";

const BillingSettings = () => {
  const [activeTab, setActiveTab] = useState("visitTypes");
  const [visitTypes, setVisitTypes] = useState([]);
  const [medicalActions, setMedicalActions] = useState([]);
  const [medicationPricing, setMedicationPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  const tabs = [
    { id: "visitTypes", label: "Visit Types", icon: Activity },
    { id: "medicalActions", label: "Medical Actions", icon: Settings },
    { id: "medications", label: "Medication Pricing", icon: Pill },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [visitTypesData, actionsData, medicationsData] = await Promise.all([
        medicalDB.getAllVisitTypes(false), // Include inactive
        medicalDB.getAllMedicalActions({ activeOnly: false }),
        medicalDB.getAllMedicationPricing(),
      ]);

      setVisitTypes(visitTypesData);
      setMedicalActions(actionsData);
      setMedicationPricing(medicationsData);
    } catch (error) {
      console.error("Error loading billing settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData(getEmptyFormData());
    setShowAddForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingItem(null);
    setFormData({});
  };

  const getEmptyFormData = () => {
    switch (activeTab) {
      case "visitTypes":
        return {
          name: "",
          defaultPrice: 0,
          description: "",
          isActive: true,
        };
      case "medicalActions":
        return {
          name: "",
          defaultPrice: 0,
          category:
            medicalDB.MEDICAL_ACTION_CATEGORIES?.DIAGNOSTIC || "diagnostic",
          description: "",
          isActive: true,
        };
      case "medications":
        return {
          medicationName: "",
          defaultPrice: 0,
          category: "general",
        };
      default:
        return {};
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (activeTab === "visitTypes") {
        if (editingItem) {
          await medicalDB.updateVisitType(editingItem.id, formData);
        } else {
          await medicalDB.createVisitType(formData);
        }
      } else if (activeTab === "medicalActions") {
        if (editingItem) {
          await medicalDB.updateMedicalAction(editingItem.id, formData);
        } else {
          await medicalDB.createMedicalAction(formData);
        }
      } else if (activeTab === "medications") {
        await medicalDB.setMedicationPrice(
          formData.medicationName,
          formData.defaultPrice,
          formData.category
        );
      }

      await loadData();
      handleCancel();
    } catch (error) {
      console.error("Error saving item:", error);
      alert("Error saving item. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      if (activeTab === "visitTypes") {
        await medicalDB.updateVisitType(item.id, {
          isActive: !item.isActive,
        });
      } else if (activeTab === "medicalActions") {
        await medicalDB.updateMedicalAction(item.id, {
          isActive: !item.isActive,
        });
      }
      await loadData();
    } catch (error) {
      console.error("Error toggling active status:", error);
    }
  };

  const renderVisitTypes = () => (
    <div className="space-y-4">
      {visitTypes.map((visitType) => (
        <div
          key={visitType.id}
          className={`p-4 border rounded-lg ${
            visitType.isActive
              ? "border-gray-200 bg-white"
              : "border-gray-300 bg-gray-50"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h3
                  className={`font-medium ${
                    visitType.isActive ? "text-gray-900" : "text-gray-500"
                  }`}
                >
                  {visitType.name}
                </h3>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    visitType.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {visitType.isActive ? "Active" : "Inactive"}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {visitType.description}
              </p>
              <p className="text-lg font-semibold text-blue-600 mt-2">
                ₪{visitType.defaultPrice.toFixed(2)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleToggleActive(visitType)}
                className={`p-2 rounded-lg transition-colors ${
                  visitType.isActive
                    ? "text-red-600 hover:bg-red-50"
                    : "text-green-600 hover:bg-green-50"
                }`}
                title={visitType.isActive ? "Deactivate" : "Activate"}
              >
                {visitType.isActive ? (
                  <X className="h-4 w-4" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={() => handleEdit(visitType)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit"
              >
                <Edit className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderMedicalActions = () => {
    const groupedActions = medicalActions.reduce((acc, action) => {
      if (!acc[action.category]) {
        acc[action.category] = [];
      }
      acc[action.category].push(action);
      return acc;
    }, {});

    return (
      <div className="space-y-6">
        {Object.entries(groupedActions).map(([category, actions]) => (
          <div key={category} className="space-y-3">
            <h3 className="text-lg font-medium text-gray-900 capitalize">
              {category} Actions
            </h3>
            <div className="space-y-3">
              {actions.map((action) => (
                <div
                  key={action.id}
                  className={`p-4 border rounded-lg ${
                    action.isActive
                      ? "border-gray-200 bg-white"
                      : "border-gray-300 bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h4
                          className={`font-medium ${
                            action.isActive ? "text-gray-900" : "text-gray-500"
                          }`}
                        >
                          {action.name}
                        </h4>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            action.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {action.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {action.description}
                      </p>
                      <p className="text-lg font-semibold text-blue-600 mt-2">
                        ₪{action.defaultPrice.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(action)}
                        className={`p-2 rounded-lg transition-colors ${
                          action.isActive
                            ? "text-red-600 hover:bg-red-50"
                            : "text-green-600 hover:bg-green-50"
                        }`}
                        title={action.isActive ? "Deactivate" : "Activate"}
                      >
                        {action.isActive ? (
                          <X className="h-4 w-4" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(action)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMedicationPricing = () => {
    const groupedMedications = medicationPricing.reduce((acc, med) => {
      if (!acc[med.category]) {
        acc[med.category] = [];
      }
      acc[med.category].push(med);
      return acc;
    }, {});

    return (
      <div className="space-y-6">
        {Object.entries(groupedMedications).map(([category, medications]) => (
          <div key={category} className="space-y-3">
            <h3 className="text-lg font-medium text-gray-900 capitalize">
              {category} Medications
            </h3>
            <div className="space-y-3">
              {medications.map((medication) => (
                <div
                  key={medication.id}
                  className="p-4 border border-gray-200 rounded-lg bg-white"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">
                        {medication.medicationName}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Category: {medication.category}
                      </p>
                      <p className="text-lg font-semibold text-blue-600 mt-2">
                        ₪{medication.defaultPrice.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(medication)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {editingItem ? "Edit" : "Add"}{" "}
            {activeTab === "visitTypes"
              ? "Visit Type"
              : activeTab === "medicalActions"
              ? "Medical Action"
              : "Medication Pricing"}
          </h3>
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {activeTab === "visitTypes" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Normal Check-up"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Price (₪)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.defaultPrice || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      defaultPrice: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of this visit type"
                />
              </div>
            </>
          )}

          {activeTab === "medicalActions" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Blood Test"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={formData.category || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="diagnostic">Diagnostic</option>
                  <option value="therapeutic">Therapeutic</option>
                  <option value="preventive">Preventive</option>
                  <option value="administrative">Administrative</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Price (₪)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.defaultPrice || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      defaultPrice: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of this action"
                />
              </div>
            </>
          )}

          {activeTab === "medications" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Medication Name
                </label>
                <input
                  type="text"
                  value={formData.medicationName || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, medicationName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Paracetamol"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., general, antibiotic, pain"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Price (₪)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.defaultPrice || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      defaultPrice: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="h-12 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Billing Settings</h1>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add{" "}
          {activeTab === "visitTypes"
            ? "Visit Type"
            : activeTab === "medicalActions"
            ? "Action"
            : "Medication"}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <IconComponent className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {activeTab === "visitTypes" && renderVisitTypes()}
        {activeTab === "medicalActions" && renderMedicalActions()}
        {activeTab === "medications" && renderMedicationPricing()}
      </div>

      {/* Form Modal */}
      {showAddForm && renderForm()}
    </div>
  );
};

export default BillingSettings;
